import { useState, useEffect } from "react";
import { IRequestCases, IIndexedCase } from "src/share/types/case/searchCase";
import { CasesService } from "src/share/services/cases";
import useRouteState from "src/share/hooks/useRouteState";
import useDidUpdateEffect from "src/share/hooks/useDidUpdateEffect";
import { momentToMiliseconds } from "src/share/utils/utils";
import { goToTop } from "../utils/goToTop";

interface ICasesResults {
  results: IIndexedCase[];
  total: number;
}

export const useCases = (options: IRequestCases): ICasesReturned => {
  const { routeState } = useRouteState();
  const [cases, setCases] = useState<ICasesResults>({ total: 0, results: [] });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const getSearchRequest = () => ({
    ...options,
    range:
      options.range && options.range.startDate && options.range.endDate
        ? {
            missingSince: {
              gte: momentToMiliseconds(options.range.startDate),
              lte: momentToMiliseconds(options.range.endDate),
            },
          }
        : null,
  });
  const getCases = async (): Promise<void> => {
    try {
      setIsLoading(true);

      if (!options.isAppended) {
        setCases({ total: 0, results: [] });
        goToTop();
      }
      const request = getSearchRequest();
      const {
        cases: { results, total },
      } = await CasesService.getCases(request);
      setCases((prevCases) => ({
        ...(options.isAppended
          ? { results: [...prevCases.results, ...results], total }
          : { results, total }),
      }));
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (routeState) {
      setCases(routeState.state);
      setIsLoading(false);
    } else {
      getCases();
    }
  }, []);
  useDidUpdateEffect(() => {
    if (!options.isRouteState) {
      getCases();
    }
  }, [options]);
  return {
    cases,
    isLoading,
  };
};

interface ICasesReturned {
  cases: ICasesResults;
  isLoading: boolean;
}
